/* ============================================================
   SINGSPACE ADMIN - HOOK EDITEUR D'ESPACE (Refactorisé)
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { SpaceTypeWithRelations } from '@singspace/common';
import { apiService } from '../services/api';
import { ALLOWED_SCALAR_FIELDS } from '../constants/space.constants';

interface UseSpaceEditorReturn {
  space: SpaceTypeWithRelations | null; isLoading: boolean; error: string | null; isSaving: boolean; isDirty: boolean;
  updateField: (field: string, value: any) => void;
  updateFormSections: (sections: any[]) => void;
  updatePricingGroups: (groups: any[]) => void;
  updateDisplay: (key: string, value: any) => void;
  updateRequesterConfig: (config: any) => void;
  updateCapacityBrackets: (brackets: any[]) => void;
  save: () => Promise<boolean>;
  saveWithData: (data: Record<string, any>) => Promise<boolean>;
  saveDisplay: (displayData: Record<string, any>) => Promise<boolean>;
  saveFormSections: (formSections: any[]) => Promise<boolean>;
  reload: () => Promise<void>;
}

export function useSpaceEditor(spaceId: string | undefined): UseSpaceEditorReturn {
  const [space, setSpace] = useState<SpaceTypeWithRelations | null>(null);
  const [original, setOriginal] = useState<SpaceTypeWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = JSON.stringify(space) !== JSON.stringify(original);

  const load = useCallback(async (isReload = false) => {
    if (!spaceId) return;
    if (!isReload) setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.getSpace(spaceId);
      const data = res.data.data;
      setSpace(data);
      setOriginal(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Erreur chargement");
    } finally { if (!isReload) setIsLoading(false); }
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);

  const updateField = useCallback((f: string, v: any) => setSpace((p: any) => p ? { ...p, [f]: v } : p), []);
  const updateFormSections = useCallback((s: any[]) => setSpace((p: any) => p ? { ...p, formSections: s } : p), []);
  const updatePricingGroups = useCallback((g: any[]) => setSpace((p: any) => p ? { ...p, pricingGroups: g } : p), []);
  const updateDisplay = useCallback((k: string, v: any) => setSpace((p: any) => p ? { ...p, [k]: v } : p), []);
  const updateRequesterConfig = useCallback((c: any) => setSpace((p: any) => p ? { ...p, requesterConfig: c } : p), []);
  const updateCapacityBrackets = useCallback((b: any[]) => setSpace((p: any) => p ? { ...p, capacityBrackets: b } : p), []);

  const saveWithData = useCallback(async (data: Record<string, any>): Promise<boolean> => {
    if (!spaceId) return false;
    setIsSaving(true);
    setError(null);
    try {
      const scalarData: Record<string, any> = {};
      for (const key of ALLOWED_SCALAR_FIELDS) { if (key in data) scalarData[key] = data[key]; }
      if (Object.keys(scalarData).length > 0) await apiService.updateSpaceScalars(spaceId, scalarData);
      if ('eventTypes' in data && Array.isArray(data.eventTypes)) await apiService.updateSpaceEventTypes(spaceId, data.eventTypes);
      const merged = { ...scalarData, ...( 'eventTypes' in data ? { eventTypes: data.eventTypes } : {}) };
      setSpace((p: any) => p ? { ...p, ...merged } : p);
      setOriginal((p: any) => p ? { ...p, ...merged } : p);
      return true;
    } catch (err: any) { setError(err?.response?.data?.error || err?.message || 'Erreur sauvegarde'); return false;
    } finally { setIsSaving(false); }
  }, [spaceId]);

  const saveDisplay = useCallback(async (displayData: Record<string, any>): Promise<boolean> => {
    if (!spaceId) return false;
    setIsSaving(true); setError(null);
    try {
      await apiService.updateSpaceDisplay(spaceId, displayData);
      setSpace((p: any) => p ? { ...p, ...displayData } : p);
      setOriginal((p: any) => p ? { ...p, ...displayData } : p);
      return true;
    } catch (err: any) { setError(err?.response?.data?.error || 'Erreur sauvegarde'); return false;
    } finally { setIsSaving(false); }
  }, [spaceId]);

  const saveFormSections = useCallback(async (fs: any[]): Promise<boolean> => {
    if (!spaceId) return false;
    setIsSaving(true); setError(null);
    try {
      await apiService.updateSpaceFormSections(spaceId, fs);
      setSpace((p: any) => p ? { ...p, formSections: fs } : p);
      setOriginal((p: any) => p ? { ...p, formSections: fs } : p);
      return true;
    } catch (err: any) { setError(err?.response?.data?.error || 'Erreur sauvegarde'); return false;
    } finally { setIsSaving(false); }
  }, [spaceId]);

  const save = useCallback(async () => spaceId && space ? saveWithData(space) : false, [spaceId, space, saveWithData]);
  const reload = useCallback(async () => { await load(true); }, [load]);

  return { space, isLoading, error, isSaving, isDirty, updateField, updateFormSections, updatePricingGroups, updateDisplay, updateRequesterConfig, updateCapacityBrackets, save, saveWithData, saveDisplay, saveFormSections, reload };
}
